<template>
  <Navbar />
  <div class="register">
    <RegProgressBar :currentStep="3" />

    <!-- Default 視圖：本站引導 -->
    <div class="complete-card" v-if="!showOtherSites">
      <CheckCircleTwoTone
        :twoToneColor="['#ffffff', 'var(--color-primary)']"
        class="success-icon"
      />
      <h2 class="success-title">共通會員帳號註冊完成</h2>
      <p class="success-subtitle">
        歡迎使用臺灣 SNOMED CT 網站！<br />您現在可以使用同一組帳號登入以下網站：
      </p>

      <div class="button-group">
        <a-button class="next-step primary-btn" @click="goToSignIn">
          前往 SNOMED CT
        </a-button>
        <a-button class="next-step outline-btn" @click="showOtherSites = true">
          前往其他網站
        </a-button>
      </div>
    </div>

    <!-- Other websites 視圖：所有共通會員計畫網站 -->
    <div class="complete-card" v-else>
      <div class="other-sites-header">
        <div class="back-btn" @click="showOtherSites = false" title="返回">
          <LeftOutlined class="back-arrow-icon" />
        </div>
        <h2 class="other-sites-title">前往 其他共通會員計畫網站</h2>
      </div>

      <div class="other-sites-list">
        <a-button
          v-for="(site, index) in otherSites"
          :key="index"
          class="next-step outline-btn"
          @click="goToUrl(site.url)"
        >
          前往 {{ site.name }}
        </a-button>
      </div>
    </div>
  </div>
</template>
<script setup>
import Navbar from "../components/Navbar.vue";
import RegProgressBar from "../components/RegProgressBar.vue";
import { CheckCircleTwoTone, LeftOutlined } from "@ant-design/icons-vue";
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { PROJECT_WEBSITE_LINKS } from "../services/api";

const { t } = useI18n();
const showOtherSites = ref(false);

const clearRegistrationSessionStorage = () => {
  const itemsToRemove = [
    "emailVerifyMail",
    "emailVerifyToken",
    "registerName",
    "registerSource",
    "registerStepCompleted",
    "registerIdNumber",
    "registerAddress",
    "registerNationality",
  ];

  itemsToRemove.forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

onMounted(() => {
  clearRegistrationSessionStorage();
});

const goToSignIn = () => {
  const lastVisitedPath = localStorage.getItem("lastVisitedPath");
  if (lastVisitedPath) {
    window.location.href = lastVisitedPath;
  } else {
    window.location.href = "/sign-in";
  }
};

const goToUrl = (url) => {
  window.location.href = url;
};

// 其他共通會員計畫網站（排除自身 SNOMED CT）
const otherSites = [
  { name: "臺灣醫療資訊標準大平台", url: PROJECT_WEBSITE_LINKS.medStandard },
  { name: "臺灣健康規則空間", url: PROJECT_WEBSITE_LINKS.thrs },
  { name: "臺灣健康應用空間", url: PROJECT_WEBSITE_LINKS.thas },
  { name: "SNOMED CT", url: PROJECT_WEBSITE_LINKS.snomedCt },
  { name: "LOINC", url: PROJECT_WEBSITE_LINKS.loinc },
  { name: "RxNorm", url: PROJECT_WEBSITE_LINKS.rxnorm },
];
</script>
<style scoped>
.register {
  width: 100%;
  min-height: 100vh;
  background-color: var(--color-primary-bg);
  background: linear-gradient(180deg, #f9f9f9, #baddff);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 16px 24px;
  box-sizing: border-box;
}

.complete-card {
  width: 100%;
  max-width: 550px;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 72px 39px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  margin-top: 30px;
}

.success-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.success-title {
  margin-top: 21px;
  font-size: 30px;
  font-weight: 700;
  color: #000;
  margin-bottom: 8px;
  text-align: center;
}

.success-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #696969;
  margin: 0;
  text-align: center;
}

.button-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 45px;
  margin-bottom: 33px;
}

.next-step {
  width: 100%;
  height: 64px;
  border-radius: 999px;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-btn {
  background-color: var(--color-primary, #0068a8);
  color: #ffffff;
  border: none;
}

.primary-btn:hover {
  background-color: var(--color-primary-hover, #005a93);
  color: #ffffff;
}

.outline-btn {
  background-color: #ffffff;
  color: var(--color-primary, #0068a8);
  border: 1px solid var(--color-primary, #0068a8);
}

.outline-btn:hover {
  background-color: #f9f9f9;
  color: var(--color-primary, #0068a8);
  border-color: var(--color-primary, #0068a8);
}

/* Other sites 視圖 */
.other-sites-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 32px;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.back-btn:hover {
  background-color: #f0f0f0;
}

.back-arrow-icon {
  font-size: 20px;
  color: #333;
}

.other-sites-title {
  font-size: 26px;
  font-weight: 700;
  color: #000;
  margin: 0;
  text-align: center;
}

.other-sites-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (max-width: 768px) {
  .register {
    padding: 48px 16px 24px;
  }
  .complete-card {
    padding: 32px 24px;
  }
}
</style>
